import type { RechargePlan } from "./subscriberRecharge";
import { validityLabel } from "./subscriberRecharge";

/**
 * Plan composition step 5 (owner 2026-10-26, D2): the subscriber picks a
 * BASIC plan, then its duration -- 30 days first, the longer terms offered
 * with what they save. A basic plan is every RADIUS plan whose linked price
 * row has the same product and speed (radaccess v1.178 carries product,
 * speed_mbps and term_days on each plan); a plan without them is a basic plan
 * of its own, with its one duration. Nothing here prices anything.
 */
export interface PlanFamily {
  key: string;
  label: string;
  /** Its plans, shortest term first. */
  plans: RechargePlan[];
}

const PRODUCT_WORDS: Record<string, string> = { residential: "Residential", commercial: "Commercial", hotspot: "Hotspot" };

export function familyKey(p: RechargePlan): string {
  return p.product && p.speed_mbps ? `${p.product}/${p.speed_mbps}` : `plan:${p.groupname}`;
}

export function familyLabel(p: RechargePlan): string {
  if (!p.product || !p.speed_mbps) return p.groupname;
  return `${PRODUCT_WORDS[p.product] ?? p.product} ${p.speed_mbps} Mbps`;
}

/** The plan's term in days: its label, else whole days of Expire-After; 0 when neither. */
export function termDays(p: RechargePlan): number {
  const t = Number(p.term_days);
  if (Number.isInteger(t) && t > 0) return t;
  return p.expireafter > 0 && p.expireafter % 86400 === 0 ? p.expireafter / 86400 : 0;
}

/** "30 days", "3 months", "6 months", "12 months", "1 day"; else the plan's own validity. */
export function durationLabel(p: RechargePlan): string {
  const d = termDays(p);
  if (d === 365) return "12 months";
  if (d > 30 && d % 30 === 0) return `${d / 30} months`;
  if (d > 0) return d === 1 ? "1 day" : `${d} days`;
  return validityLabel(p);
}

/** Basic plans in the order they first appear; each one's plans by term, shortest first. */
export function planFamilies(plans: RechargePlan[]): PlanFamily[] {
  const byKey = new Map<string, PlanFamily>();
  for (const p of plans) {
    const k = familyKey(p);
    const f = byKey.get(k) ?? { key: k, label: familyLabel(p), plans: [] };
    f.plans.push(p);
    byKey.set(k, f);
  }
  const out = [...byKey.values()];
  for (const f of out) f.plans.sort((a, b) => termDays(a) - termDays(b) || a.groupname.localeCompare(b.groupname));
  return out;
}

/** The family a plan belongs to, or undefined. */
export function familyOf(families: PlanFamily[], groupname: string): PlanFamily | undefined {
  return families.find((f) => f.plans.some((p) => p.groupname === groupname));
}

/**
 * The duration chosen first when a basic plan is picked: its 30-day plan,
 * else its shortest one that may be sold (why returns "" for a sellable plan).
 */
export function defaultDuration(f: PlanFamily, why: (p: RechargePlan) => string = () => ""): RechargePlan | undefined {
  const sellable = f.plans.filter((p) => why(p) === "");
  return sellable.find((p) => termDays(p) === 30) ?? sellable[0];
}

/**
 * What a pin-backed plan spends, in words (owner 2026-10-26: a pin pays for
 * 30 days; more months spend one pin each unless a pin of the plan's own
 * length is in stock): "uses 1 pin", "uses 3 pins, or one 3-month pin", "uses
 * 12 pins, or one yearly pin". The same words as radaccess model.PinsNote;
 * "" for a plan that spends no pin.
 */
export function pinWords(p: RechargePlan | undefined): string {
  if (!p || p.pin !== "spends") return "";
  const d = termDays(p);
  const m = d > 0 ? Math.max(1, Math.floor((d + 15) / 30)) : 1;
  if (m < 2) return "uses 1 pin";
  return `uses ${m} pins, or one ${m === 12 ? "yearly" : `${m}-month`} pin`;
}
